'use client';

import React, { useState, useEffect } from 'react';

interface NavigationProps {
  activeSection: string;
}

const navItems = [
  { id: 'about', label: 'About', color: '#7c3aed', textColor: '#ffffff' },
  { id: 'skills', label: 'Skills', color: '#06b6d4', textColor: '#ffffff' },
  { id: 'projects', label: 'Work', color: '#5b21b6', textColor: '#ffffff' },
  { id: 'services', label: 'Services', color: '#0891b2', textColor: '#ffffff' },
  { id: 'contact', label: 'Contact', color: '#7c3aed', textColor: '#ffffff' },
];

const menuLinks = [
  { id: 'about', label: 'About Me' },
  { id: 'skills', label: 'Skills & Tools' },
  { id: 'projects', label: 'Projects' },
  { id: 'services', label: 'Services' },
  { id: 'testimonials', label: 'Testimonials' },
  { id: 'contact', label: 'Contact' },
];

export default function Navigation({ activeSection }: NavigationProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id: string) => {
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth' });
    }
    setMenuOpen(false);
  };

  return (
    <>
      {/* Top Navigation Bar */}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
          scrolled ? 'bg-light-warm shadow-sm' : 'bg-transparent'
        }`}
      >
        <div className="flex items-center justify-between px-4 md:px-10 py-4 min-w-0">
          {/* Hamburger */}
          <button
            onClick={() => setMenuOpen(!menuOpen)}
            className="flex flex-col gap-[5px] w-8 h-8 justify-center items-start cursor-pointer z-50 group flex-shrink-0"
            aria-label="Toggle menu"
          >
            <span
              className={`block h-[2px] bg-primary transition-all duration-300 ${
                menuOpen ? 'w-6 rotate-45 translate-y-[7px]' : 'w-6'
              }`}
            />
            <span
              className={`block h-[2px] bg-primary transition-all duration-300 ${
                menuOpen ? 'opacity-0 w-0' : 'w-4'
              }`}
            />
            <span
              className={`block h-[2px] bg-primary transition-all duration-300 ${
                menuOpen ? 'w-6 -rotate-45 -translate-y-[7px]' : 'w-6'
              }`}
            />
          </button>

          {/* Logo / Name */}
          <button
            onClick={() => scrollToSection('hero')}
            className="font-condensed font-bold text-xl tracking-widest uppercase text-primary hover:text-primary-dark transition-colors duration-300 flex-shrink-0"
          >
            RAMSON
          </button>

          {/* CTA */}
          <button
            onClick={() => scrollToSection('contact')}
            className="flex-shrink-0 font-condensed font-bold text-xs tracking-widest uppercase text-primary border-b-2 border-transparent hover:border-primary-dark hover:text-primary-dark transition-all duration-300 pb-0.5 whitespace-nowrap"
          >
            Book a Meeting
          </button>
        </div>
      </header>

      {/* Vertical Side Tabs */}
      <div className="fixed left-0 top-1/2 -translate-y-1/2 z-40 flex flex-col hidden lg:flex">
        {navItems.map((item) => (
          <button
            key={item.id}
            onClick={() => scrollToSection(item.id)}
            className={`vertical-tab transition-all duration-300 ${
              activeSection === item.id ? 'opacity-100' : 'opacity-80 hover:opacity-100'
            }`}
            style={{
              backgroundColor: item.color,
              color: item.textColor,
              borderRight: activeSection === item.id ? '3px solid #26262B' : '3px solid transparent',
            }}
            aria-label={`Navigate to ${item.label}`}
          >
            {item.label}
          </button>
        ))}
      </div>

      {/* Full Screen Menu Overlay */}
      <div
        className={`fixed inset-0 z-40 bg-dark transition-all duration-500 ${
          menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="flex flex-col justify-center items-start h-full px-12 md:px-20">
          <nav className="space-y-2">
            {menuLinks.map((link, index) => (
              <button
                key={link.id}
                onClick={() => scrollToSection(link.id)}
                className={`block font-condensed font-bold uppercase text-light-warm transition-all duration-300 hover:text-primary hover:translate-x-2 ${
                  menuOpen ? 'opacity-100' : 'opacity-0'
                }`}
                style={{
                  fontSize: 'clamp(2rem, 5vw, 4rem)',
                  lineHeight: '1.1',
                  transitionDelay: menuOpen ? `${index * 60}ms` : '0ms',
                  transform: menuOpen ? 'translateX(0)' : 'translateX(-20px)',
                }}
              >
                {link.label}
              </button>
            ))}
          </nav>

          <div className="mt-12 flex flex-col gap-2">
            <a
              href="mailto:ramsonndanjim@hotmail.com"
              className="font-sans text-sm text-secondary-dark hover:text-primary transition-colors duration-300"
            >
              ramsonndanjim@hotmail.com
            </a>
            <a
              href="tel:+32484616927"
              className="font-sans text-sm text-secondary-dark hover:text-primary transition-colors duration-300"
            >
              +32 484 61 69 27
            </a>
          </div>

          <div className="absolute bottom-8 right-12 font-condensed text-xs tracking-widest uppercase text-secondary-dark">
            Belgium
          </div>
        </div>
      </div>
    </>
  );
}